import { useMemo } from "react";
import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import EmptyState from "@/pages/admin/components/EmptyState";
import EstadoBadge from "@/pages/admin/components/EstadoBadge";
import StatCard from "@/pages/admin/components/StatCard";
import EstadoDistribution from "./components/EstadoDistribution";
import WeeklyActivity from "./components/WeeklyActivity";
import { useSolicitudes } from "@/hooks/useSolicitudes";
import { useVehiculos } from "@/hooks/usePublicContent";
import { useAuth } from "@/context/AuthContext";
import { estadosSolicitud } from "@/lib/solicitudEstados";
import { formatFecha } from "@/lib/format";

const PENDIENTES = ["en_revision", "contactado", "cotizacion"];
const CONFIRMADAS = ["confirmada", "en_proceso"];

export default function AdminPanel() {
  const { user } = useAuth();
  const { solicitudes, loading, error, reload } = useSolicitudes();
  const flota = useVehiculos();

  const counts = useMemo(() => {
    const base: Record<string, number> = {};
    estadosSolicitud.forEach((estado) => {
      base[estado.value] = 0;
    });
    solicitudes.forEach((solicitud) => {
      base[solicitud.estado] = (base[solicitud.estado] ?? 0) + 1;
    });
    return base;
  }, [solicitudes]);

  const total = solicitudes.length;
  const nuevas = counts.nueva ?? 0;
  const pendientes = PENDIENTES.reduce((acc, key) => acc + (counts[key] ?? 0), 0);
  const confirmadas = CONFIRMADAS.reduce((acc, key) => acc + (counts[key] ?? 0), 0);
  const completadas = counts.completada ?? 0;
  const canceladas = counts.cancelada ?? 0;

  const flotaDisponible = flota.filter((v) => v.estado === "disponible").length;
  const flotaReservada = flota.filter((v) => v.estado === "reservado").length;

  const weekly = useMemo(() => {
    const days: { label: string; count: number }[] = [];
    const today = new Date();
    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const key = date.toISOString().slice(0, 10);
      const count = solicitudes.filter((s) => s.creado_en?.slice(0, 10) === key).length;
      days.push({
        label: date.toLocaleDateString("es-PE", { weekday: "short" }).replace(".", ""),
        count,
      });
    }
    return days;
  }, [solicitudes]);

  const recientes = solicitudes.slice(0, 6);
  const displayName =
    (user?.user_metadata?.nombre as string | undefined) || user?.email?.split("@")[0] || "administrador";

  return (
    <div>
      <AdminPageHeader
        title={`Hola, ${displayName}`}
        description="Resumen general de solicitudes de servicio y estado de la flota de AXONIA GROUP."
        action={
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={reload} disabled={loading}>
            Actualizar
          </Button>
        }
      />

      {error && (
        <div className="mb-6 flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {error}
          </p>
          <Button variant="primary" size="sm" icon="ri-refresh-line" onClick={reload}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Indicadores principales */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Solicitudes nuevas" value={nuevas} icon="ri-notification-3-line" tone="accent" hint="Requieren tu atención" />
        <StatCard label="Pendientes" value={pendientes} icon="ri-time-line" tone="secondary" hint="En revisión o cotización" />
        <StatCard label="Confirmadas" value={confirmadas} icon="ri-calendar-check-line" tone="primary" hint="Confirmadas o en proceso" />
        <StatCard label="Completadas" value={completadas} icon="ri-checkbox-circle-line" tone="secondary" hint="Servicios finalizados" />
      </div>

      {/* Indicadores secundarios */}
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-label uppercase tracking-wide text-foreground-500">Total de solicitudes</p>
          <p className="mt-1 text-2xl font-heading font-bold text-foreground-950">{total}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-label uppercase tracking-wide text-foreground-500">Canceladas</p>
          <p className="mt-1 text-2xl font-heading font-bold text-foreground-950">{canceladas}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-label uppercase tracking-wide text-foreground-500">Flota disponible</p>
          <p className="mt-1 text-2xl font-heading font-bold text-foreground-950">{flotaDisponible}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-label uppercase tracking-wide text-foreground-500">Flota reservada</p>
          <p className="mt-1 text-2xl font-heading font-bold text-foreground-950">{flotaReservada}</p>
        </div>
      </div>

      {/* Gráficos */}
      {loading && total === 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1].map((key) => (
            <div key={key} className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <EstadoDistribution counts={counts} total={total} />
          <WeeklyActivity data={weekly} />
        </div>
      )}

      {/* Solicitudes recientes */}
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h2 className="text-base font-heading font-semibold text-foreground-950">
            Solicitudes recientes
          </h2>
          <Link
            to="/admin/solicitudes"
            className="flex items-center gap-1 text-sm font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            Ver todas
            <i className="ri-arrow-right-s-line text-base leading-none" aria-hidden="true"></i>
          </Link>
        </div>

        {recientes.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon="ri-inbox-line"
              title="Aún no hay solicitudes"
              description="Cuando un cliente envíe el formulario de servicio, aparecerá aquí al instante."
            />
          </div>
        ) : (
          <ul className="divide-y divide-background-200">
            {recientes.map((solicitud) => (
              <li key={solicitud.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-label font-semibold text-foreground-900">
                    <span className="text-primary-700">{solicitud.numero}</span>
                    <span className="text-foreground-300">·</span>
                    <span className="truncate">{solicitud.nombre_cliente}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {solicitud.tipo_servicio} · {formatFecha(solicitud.creado_en)}
                  </p>
                </div>
                <EstadoBadge estado={solicitud.estado} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}